import { Icon, IconButton, Wordmark } from "@obsina/design-system";
import { GripVertical, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent,
  PointerEvent,
  ReactNode,
} from "react";
import type { AuthenticatedAccount } from "./App";
import type { AppMessages } from "./messages";
import { AccountMenu, PrimaryNav } from "./navigation";
import type { AppView } from "./navigation";
import { useSidebarPreferences } from "./sidebar-preferences";
import { SidebarActivity } from "./activity/ActivityIndicator";
import {
  UI_SIDEBAR_DEFAULT_WIDTH_PX,
  UI_SIDEBAR_MAX_WIDTH_PX,
  UI_SIDEBAR_MIN_WIDTH_PX,
} from "@obsina/domain";
import "./app-shell-sidebar.css";

export interface AppShellProps {
  messages: AppMessages;
  activeView: AppView;
  /** Present only when the shared navigation is mounted (the full application). */
  onNavigate?: (view: AppView) => void;
  account?: AuthenticatedAccount | null;
  /** Already-composed sign-out handler owned by the page (keeps per-view error handling). */
  onSignOut?: () => void;
  signingOut?: boolean;
  /**
   * Optional page-scoped search control, rendered above the navigation. Only the
   * pages where searching is in scope pass it; the sync-control surface never does.
   */
  search?: ReactNode;
  /** Optional page-scoped status shown in the sidebar footer (e.g. connection state). */
  status?: ReactNode;
  /**
   * Fixed/overlay siblings that belong to the shell but sit outside `<main>`
   * (toast region, the dev-only preview switcher).
   */
  overlays?: ReactNode;
  children: ReactNode;
}

interface ResizeSession {
  readonly pointerId: number;
  readonly handle: HTMLDivElement;
  readonly startX: number;
  readonly startWidth: number;
  latestWidth: number;
  changed: boolean;
}

type ShellStyle = CSSProperties & { "--app-rail-width": string };

/**
 * The single shared application frame: a resizable left rail beside the
 * scrollable `<main>` content. On narrow viewports the rail reflows into a top
 * bar so every control stays reachable at 320 CSS px.
 */
export function AppShell({
  messages,
  activeView,
  onNavigate,
  account = null,
  onSignOut,
  signingOut = false,
  search,
  status,
  overlays,
  children,
}: AppShellProps) {
  const { preferences, setSidebarCollapsed, commitSidebarExpandedWidth } =
    useSidebarPreferences();
  const collapsed = preferences.sidebarCollapsed;
  const [liveWidth, setLiveWidth] = useState(
    preferences.sidebarExpandedWidthPx,
  );
  const [resizing, setResizing] = useState(false);
  const resizeSessionRef = useRef<ResizeSession | null>(null);
  const cleanupResizeRef = useRef<(() => void) | null>(null);
  const sidebarId = useId();

  useEffect(() => {
    if (!resizing) setLiveWidth(preferences.sidebarExpandedWidthPx);
  }, [preferences.sidebarExpandedWidthPx, resizing]);

  useEffect(
    () => () => {
      cleanupResizeRef.current?.();
      cleanupResizeRef.current = null;
    },
    [],
  );

  const finishResize = useCallback(
    (commit: boolean) => {
      const session = resizeSessionRef.current;
      if (!session) return;
      cleanupResizeRef.current?.();
      cleanupResizeRef.current = null;
      resizeSessionRef.current = null;
      setResizing(false);
      try {
        if (session.handle.hasPointerCapture(session.pointerId)) {
          session.handle.releasePointerCapture(session.pointerId);
        }
      } catch {
        // Pointer capture is best-effort; global listeners still finish safely.
      }
      if (commit && session.changed) {
        commitSidebarExpandedWidth(session.latestWidth);
      } else if (!commit) {
        setLiveWidth(preferences.sidebarExpandedWidthPx);
      }
    },
    [commitSidebarExpandedWidth, preferences.sidebarExpandedWidthPx],
  );

  const beginResize = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (collapsed || event.button !== 0) return;
      event.preventDefault();
      cleanupResizeRef.current?.();
      const handle = event.currentTarget;
      const session: ResizeSession = {
        pointerId: event.pointerId,
        handle,
        startX: event.clientX,
        startWidth: liveWidth,
        latestWidth: liveWidth,
        changed: false,
      };
      resizeSessionRef.current = session;
      setResizing(true);
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // jsdom and older engines may not implement pointer capture. The global
        // listeners below provide the same lifecycle guarantee.
      }

      const move = (pointerEvent: globalThis.PointerEvent) => {
        if (pointerEvent.pointerId !== session.pointerId) return;
        const nextWidth = clampSidebarWidth(
          session.startWidth + pointerEvent.clientX - session.startX,
        );
        session.latestWidth = nextWidth;
        session.changed = nextWidth !== session.startWidth;
        setLiveWidth(nextWidth);
      };
      const up = (pointerEvent: globalThis.PointerEvent) => {
        if (pointerEvent.pointerId === session.pointerId) finishResize(true);
      };
      const cancel = (pointerEvent: globalThis.PointerEvent) => {
        if (pointerEvent.pointerId === session.pointerId) finishResize(false);
      };
      const lostCapture = (pointerEvent: globalThis.PointerEvent) => {
        if (pointerEvent.pointerId === session.pointerId) finishResize(false);
      };
      const windowBlur = () => finishResize(false);
      const cleanup = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        window.removeEventListener("blur", windowBlur);
        handle.removeEventListener("lostpointercapture", lostCapture);
      };
      cleanupResizeRef.current = cleanup;
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("blur", windowBlur);
      handle.addEventListener("lostpointercapture", lostCapture);
    },
    [collapsed, finishResize, liveWidth],
  );

  const resizeWithKeyboard = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 16 : 8;
      let nextWidth: number | null = null;
      if (event.key === "ArrowLeft") nextWidth = liveWidth - step;
      else if (event.key === "ArrowRight") nextWidth = liveWidth + step;
      else if (event.key === "Home") nextWidth = UI_SIDEBAR_MIN_WIDTH_PX;
      else if (event.key === "End") nextWidth = UI_SIDEBAR_MAX_WIDTH_PX;
      if (nextWidth === null) return;
      event.preventDefault();
      const clamped = clampSidebarWidth(nextWidth);
      setLiveWidth(clamped);
      commitSidebarExpandedWidth(clamped);
    },
    [commitSidebarExpandedWidth, liveWidth],
  );

  const shellStyle: ShellStyle = {
    "--app-rail-width": `${liveWidth}px`,
  };

  return (
    <div
      className="app-shell"
      data-sidebar-resizing={resizing || undefined}
      data-sidebar-state={collapsed ? "collapsed" : "expanded"}
      style={shellStyle}
    >
      <a className="skip-link" href="#main-content">
        {messages.common.skipToContent}
      </a>
      <header className="app-sidebar" id={sidebarId}>
        <div className="app-sidebar__brand">
          <Wordmark className="app-sidebar__wordmark" />
          <span className="app-sidebar__source">{messages.sourceArea}</span>
        </div>
        {search && <div className="app-sidebar__search">{search}</div>}
        {onNavigate && (
          <PrimaryNav
            activeView={activeView}
            collapsed={collapsed}
            messages={messages}
            onNavigate={onNavigate}
          />
        )}
        <div className="app-sidebar__footer">
          {/* Two content-sized signals share one wrapping row rather than
              stacking: the page's own status (where a page provides one) and
              the shell-wide activity sign, which every page carries. */}
          <div className="app-sidebar__signals">
            {status && <div className="app-sidebar__status">{status}</div>}
            <SidebarActivity />
          </div>
          <IconButton
            aria-controls={sidebarId}
            aria-expanded={!collapsed}
            aria-label={
              collapsed ? messages.sidebar.expand : messages.sidebar.collapse
            }
            className="app-sidebar__toggle"
            icon={collapsed ? PanelLeftOpen : PanelLeftClose}
            onClick={() => setSidebarCollapsed(!collapsed)}
            size="sm"
          />
          {account && onSignOut && (
            <AccountMenu
              account={account}
              collapsed={collapsed}
              messages={messages}
              onSignOut={onSignOut}
              signingOut={signingOut}
            />
          )}
        </div>
        <div
          aria-label={messages.sidebar.resize}
          aria-orientation="vertical"
          aria-valuemax={UI_SIDEBAR_MAX_WIDTH_PX}
          aria-valuemin={UI_SIDEBAR_MIN_WIDTH_PX}
          aria-valuenow={liveWidth}
          aria-valuetext={`${liveWidth} px`}
          className="app-sidebar__resize-handle"
          onKeyDown={resizeWithKeyboard}
          onPointerDown={beginResize}
          role="separator"
          tabIndex={0}
        >
          <span aria-hidden="true" className="app-sidebar__resize-grip">
            <Icon icon={GripVertical} size={12} />
          </span>
        </div>
      </header>
      <main
        className="main"
        data-view={activeView}
        id="main-content"
        tabIndex={-1}
      >
        {children}
      </main>
      {overlays}
    </div>
  );
}

function clampSidebarWidth(widthPx: number): number {
  if (!Number.isFinite(widthPx)) return UI_SIDEBAR_DEFAULT_WIDTH_PX;
  return Math.min(
    UI_SIDEBAR_MAX_WIDTH_PX,
    Math.max(UI_SIDEBAR_MIN_WIDTH_PX, Math.round(widthPx)),
  );
}
